import React, { useState, useRef, RefObject, useEffect, useMemo } from 'react';
import {
    Button,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Alert,
    AlertTitle,
    Stack,
    TextField

} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { DatePatternFrequencyWeeks, DatePatternFrequencyMonths, DatePatternFrequencyYears, DatePatternFrequencyPeriodPay } from 'repository/timekeeper/@types/DatePattern';
import {
    FrequencyUnitTime,
    PatternStartDateEnum,
    CompensationTypeEnum,
    createDatePatternSchema,
    FormValues

} from 'schemas/DatePattern';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { DatePatternFormFields } from './DatePatternFormFields';
import { DatePatternDaysTable } from './DatePatternDaysTable';

dayjs.extend(duration);

const formatDurationToHHMM = (raw?: string | null) => {
    if (!raw) {
        return '00:00';
    }

    if (/^PT/i.test(raw)) {
        const parsed = dayjs.duration(raw);
        const totalMinutes = Math.floor(parsed.asMinutes());
        if (Number.isFinite(totalMinutes)) {
            const outHours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
            const outMinutes = String(totalMinutes % 60).padStart(2, '0');
            return `${outHours}:${outMinutes}`;
        }
    }

    const parts = raw.split(':').map(Number);
    if ((parts.length === 2 || parts.length === 3) && parts.every((part) => !Number.isNaN(part))) {
        const parsed = dayjs.duration({
            hours: parts[0] ?? 0,
            minutes: parts[1] ?? 0,
            seconds: parts[2] ?? 0,
        });
        const totalMinutes = Math.floor(parsed.asMinutes());
        const outHours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
        const outMinutes = String(totalMinutes % 60).padStart(2, '0');
        return `${outHours}:${outMinutes}`;
    }

    return raw;
};

interface UpdateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    rowToEdit: any;
    reloading: () => void;
    cleanSelected: () => void;
}

interface UpdateDatePatternsRequest {
    name: string;
    reference_date_id: number;
    frequency_unit_time: FrequencyUnitTime;
    frequency_amount: number;
    pattern_start_date: PatternStartDateEnum;
    day_week_to_start: number | null;
    day_month_to_start: number | null;
    month_to_start: number | null;
    day_period_to_start: number | null;
    compensation_type: CompensationTypeEnum | null;
    compensation_number: number | null;
    compensation_pattern_date_id: number | null;
    full_time_equivalent: string;

}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

const plusDays = (baseDate: Date, days: number): Date => {
    const nextDate = new Date(baseDate);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
};

const UpdateDatePattern: React.FC<UpdateProps> = ({ open, setOpen, rows, rowToEdit, reloading, cleanSelected }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();
    const [referenceDateOptions, setReferenceDateOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)
    const [showDaysPanel, setShowDaysPanel] = useState(false);
    const [rangeLoading, setRangeLoading] = useState(false);
    const [rangeDates, setRangeDates] = useState<string[]>([]);
    const [startDate, setStartDate] = useState(() => toIsoDate(new Date()));
    const [endDate, setEndDate] = useState(() => toIsoDate(plusDays(new Date(), 90)));

    const defaultValues: FormValues = useMemo(() => ({
        name: '',
        reference_date_id: 0,
        frequency_amount: 1,
        frequency_unit_time: FrequencyUnitTime.DIAS,
        pattern_start_date: PatternStartDateEnum.FECHA_REFERENCIA,
        day_week_to_start: 1,
        day_month_to_start: 1,
        month_to_start: 1,
        day_period_to_start: 1,
        compensation_type: CompensationTypeEnum.NONE,
        compensation_number: 0,
        compensation_pattern_date_id: 0,
        full_time_equivalent: '00:00'
    }), []);

    const initialValues = useMemo(() => rowToEdit ? {
        name: rowToEdit.name
    } : undefined, [rowToEdit]);

    const schema = useMemo(() => createDatePatternSchema({
        t,
        rows,
        initialValues
    }), [t, rows, initialValues]);

    const { control, register, reset, handleSubmit, watch, setValue, getValues, trigger, formState: { errors, isValid, isDirty } } = useForm<FormValues>({
        defaultValues,
        resolver: zodResolver(schema),
        mode: 'all',
        criteriaMode: 'all',
        reValidateMode: 'onChange',
    });

    const frequencyUnitTime = watch('frequency_unit_time');
    const pattern_start_date = watch('pattern_start_date');
    const compensation_type = watch('compensation_type');
    const nameValue = watch('name');

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                const id: number = rowToEdit.id;
                const [datePattern, accrualDates] = await Promise.allSettled([
                    timekeeperService.date_pattern.getOne(id),
                    timekeeperService.accrual_date.getAll()
                ]);

                if (datePattern.status === 'fulfilled') {
                    const data = datePattern.value;

                    // Preparar los valores para el formulario
                    const formData: FormValues = {
                        name: data.name,
                        reference_date_id: data.reference_date_id,
                        frequency_unit_time: data.frequency_unit_time,
                        frequency_amount: data.frequency_amount,
                        pattern_start_date: data.pattern_start_date,
                        day_week_to_start: 1,
                        day_month_to_start: 1,
                        month_to_start: 1,
                        day_period_to_start: 1,
                        compensation_type: data.compensation_type || CompensationTypeEnum.NONE,
                        compensation_number: data.compensation_number || 0,
                        compensation_pattern_date_id: data.compensation_pattern_date_id || 0,
                        full_time_equivalent: formatDurationToHHMM(data.full_time_equivalent)
                    };

                    // Establecer campos condicionales según el tipo de frecuencia
                    switch (data.frequency_unit_time) {
                        case FrequencyUnitTime.SEMANAS:
                            formData.day_week_to_start = (data as DatePatternFrequencyWeeks).day_week_to_start ?? 1;
                            break;
                        case FrequencyUnitTime.MESES:
                            formData.day_month_to_start = (data as DatePatternFrequencyMonths).day_month_to_start ?? 1;
                            break;
                        case FrequencyUnitTime.ANOS:
                            formData.month_to_start = (data as DatePatternFrequencyYears).month_to_start ?? 1;
                            formData.day_month_to_start = (data as DatePatternFrequencyYears).day_period_to_start ?? 1;
                            break;
                        case FrequencyUnitTime.PERIODO_PAGO:
                            formData.day_period_to_start = (data as DatePatternFrequencyPeriodPay).day_period_to_start ?? 1;
                            break;
                    }

                    reset(formData);
                }

                if (accrualDates.status === 'fulfilled') {
                    const accrualDatesData = accrualDates ? accrualDates.value : [];
                    setReferenceDateOptions(accrualDatesData);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && rowToEdit) {
            loadData();
        }
    }, [open, rowToEdit, reset]);

    const changes = () => {
        const values = getValues();
        const hasChanges =
            values.name !== '' ||
            values.reference_date_id !== 0 ||
            values.frequency_amount !== 1 ||
            values.frequency_unit_time !== FrequencyUnitTime.DIAS ||
            values.pattern_start_date !== PatternStartDateEnum.FECHA_REFERENCIA;

        if (hasChanges) {
            setAlerts(true);
        } else {
            setOpen(false);
        }
    };

    const closeForm = () => {
        reset(defaultValues);
        setOpen(false);
        setAlerts(false);
        setLoading(false);
        setShowDaysPanel(false);
        setRangeDates([]);
        cleanSelected();
    };

    const buildDatePatternPayload = (data: FormValues): UpdateDatePatternsRequest => ({
        name: data.name,
        reference_date_id: data.reference_date_id,
        frequency_unit_time: data.frequency_unit_time,
        frequency_amount: data.frequency_amount || 1,
        pattern_start_date: data.pattern_start_date,
        day_week_to_start: data.pattern_start_date !== PatternStartDateEnum.FECHA_REFERENCIA ? data.day_week_to_start : null,
        day_month_to_start: data.pattern_start_date !== PatternStartDateEnum.FECHA_REFERENCIA ? data.day_month_to_start : null,
        month_to_start: data.pattern_start_date !== PatternStartDateEnum.FECHA_REFERENCIA ? data.month_to_start : null,
        day_period_to_start: data.pattern_start_date !== PatternStartDateEnum.FECHA_REFERENCIA ? data.day_period_to_start : null,
        compensation_type: data.compensation_type === CompensationTypeEnum.NONE ? null : data.compensation_type,
        compensation_number: data.compensation_type === CompensationTypeEnum.NONE || data.compensation_type === CompensationTypeEnum.DISABLED ? null : data.compensation_number,
        compensation_pattern_date_id: data.compensation_pattern_date_id === 0 || data.compensation_pattern_date_id === -1 ? null : data.compensation_pattern_date_id,
        full_time_equivalent: data.full_time_equivalent
    });

    const fetchDateRange = async () => {
        const valid = await trigger();
        if (!valid) {
            enqueueSnackbar('Complete los campos requeridos para consultar el listado de días', { variant: 'warning' });
            return;
        }

        if (!startDate || !endDate) {
            enqueueSnackbar('Debe ingresar fecha inicial y final', { variant: 'warning' });
            return;
        }

        try {
            setRangeLoading(true);
            const payload = buildDatePatternPayload(getValues());
            const response = await timekeeperService.date_pattern.range({
                date_pattern: {
                    ...payload,
                    date_pattern_id: rowToEdit?.id
                },
                start_date: startDate,
                end_date: endDate
            });
            setRangeDates(response);
        } catch (error) {
            console.error('Error al consultar listado de días:', error);
            enqueueSnackbar('No fue posible obtener el listado de días', { variant: 'error' });
        } finally {
            setRangeLoading(false);
        }
    };

    const onSubmit = async (data: FormValues) => {
        setLoading(true);

        const datePatternsToUpdate: UpdateDatePatternsRequest = buildDatePatternPayload(data);

        try {
            await timekeeperService.date_pattern.update(rowToEdit.id, datePatternsToUpdate);
            enqueueSnackbar(t('accruals.date_patterns.dialog.alerts.update.success'), { variant: 'success' });
            setLoading(false);
            closeForm();
            reloading();
        } catch (error) {
            console.error('Error updating date pattern:', error);
            enqueueSnackbar(t('accruals.date_patterns.dialog.alerts.update.error'), { variant: 'error' });
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth={showDaysPanel ? "lg" : "sm"}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.date_patterns.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ mb: 2 }}>
                            {!showDaysPanel && (
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowDaysPanel(true)}
                                    disabled={loading || rangeLoading}
                                >
                                    Ver listado de días
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <DatePatternFormFields
                                t={t}
                                control={control}
                                register={register}
                                errors={errors}
                                setValue={setValue}
                                rows={rows}
                                referenceDateOptions={referenceDateOptions}
                                frequencyUnitTime={frequencyUnitTime}
                                patternStartDate={pattern_start_date}
                                compensationType={compensation_type || CompensationTypeEnum.NONE}
                                nameValue={nameValue}
                                disabled={loading}
                            />
                        </Box>
                    </Box>
                    {showDaysPanel && (
                        <Box
                            sx={{
                                flexBasis: { xs: '100%', md: '22%' },
                                minWidth: { xs: '100%', md: 280 },
                                borderLeft: { xs: 'none', md: '1px solid' },
                                borderColor: 'divider',
                                pl: { xs: 0, md: 2 }
                            }}
                        >
                            <Stack direction="column" spacing={1.5}>
                                <Button variant="text" color="inherit" onClick={() => setShowDaysPanel(false)}>
                                    Ocultar listado de días
                                </Button>
                                <TextField
                                    label="Desde"
                                    type="date"
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="Hasta"
                                    type="date"
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    size="small"
                                    fullWidth
                                />
                                <Button variant="contained" onClick={fetchDateRange} disabled={loading || rangeLoading}>
                                    Enviar
                                </Button>
                                <DatePatternDaysTable
                                    dates={rangeDates}
                                    loading={rangeLoading}
                                    emptyMessage="Sin datos para el rango seleccionado"
                                    maxHeight={420}
                                />
                            </Stack>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={loading || (isDirty && !isValid)} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export { UpdateDatePattern };